import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  ReactNode,
} from 'react';
import { AuthContext } from '../components/auth/AuthContext';
import { getUserSettings, updateUserSettings } from '../services/api';
import { UserSettings, UserSettingsPatch } from '../types';

interface UserSettingsContextType {
  /**
   * The user's stored settings, or null when we don't know them — in flight,
   * failed, or signed out. Every consumer must keep treating null as "offer no
   * control that saves": the Auto-Apply modal PUTs all five of its fields, so a
   * control seeded from a placeholder would overwrite real exclusion lists with
   * empty ones.
   */
  settings: UserSettings | null;
  /** True only while the GET is in flight — tells the two null states apart. */
  loading: boolean;
  /** PUT a partial update and broadcast the result to every consumer. */
  updateSettings: (patch: Partial<UserSettingsPatch>) => Promise<UserSettings>;
  /**
   * Absorb settings a component already saved on its own (the Auto-Apply
   * activation modal owns its multi-field PUT), so every surface stays in sync
   * without that save being reimplemented here.
   */
  applySettings: (settings: UserSettings) => void;
  /** Re-fetch — used by Settings to retry after a failed load. */
  refresh: () => Promise<void>;
}

const UserSettingsContext = createContext<UserSettingsContextType>({
  settings: null,
  loading: false,
  updateSettings: async () => {
    throw new Error('UserSettingsProvider is missing');
  },
  applySettings: () => {},
  refresh: async () => {},
});

export const useUserSettings = () => useContext(UserSettingsContext);

interface UserSettingsProviderProps {
  children: ReactNode;
}

/**
 * Single source for `/users/me/settings`. Before this, Settings and
 * Opportunities each ran their own GET and held their own copy; a third
 * consumer (the Applications Auto-Apply offer) would have made three states
 * that drift the moment one of them saves.
 */
export const UserSettingsProvider: React.FC<UserSettingsProviderProps> = ({ children }) => {
  const { accessToken } = useContext(AuthContext);
  const [settings, setSettings] = useState<UserSettings | null>(null);
  const [loading, setLoading] = useState(false);

  const refresh = useCallback(async () => {
    // The provider wraps the whole app (incl. /signin and registration); skip
    // the call with no session so we don't trip the 401 interceptor.
    if (!localStorage.getItem('accessToken')) {
      setSettings(null);
      return;
    }
    setLoading(true);
    try {
      setSettings(await getUserSettings());
    } catch (error) {
      // Left null so consumers render their unavailable state. No toast: this
      // runs on app boot, where it would fire on whatever page the user is on
      // about settings they weren't looking at — Settings surfaces the failure
      // inline instead. Sentry already has it via the api.ts interceptor; the
      // log is the local breadcrumb.
      console.error('Failed to load user settings:', error);
      setSettings(null);
    } finally {
      setLoading(false);
    }
  }, []);

  // Re-runs when the token changes, so signing in loads settings without a
  // full page reload and signing out clears them.
  useEffect(() => {
    refresh();
  }, [refresh, accessToken]);

  const updateSettings = useCallback(async (patch: Partial<UserSettingsPatch>) => {
    const updated = await updateUserSettings(patch);
    setSettings(updated);
    return updated;
  }, []);

  return (
    <UserSettingsContext.Provider
      value={{ settings, loading, updateSettings, applySettings: setSettings, refresh }}
    >
      {children}
    </UserSettingsContext.Provider>
  );
};
